import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { GlobalState } from "context/GlobalContext";
import { rootProtectedPath, services } from "data/authData";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { isMboLfsQtyViewer } from "../data";
import {
	KpiStat,
	Panel,
	compactInputSx,
	outlinedButtonSx,
	primaryButtonSx,
} from "../sharedStyles";
import { downloadTable } from "../exportTable";

const COLUMN_GROUPS = [
	{
		id: "movement",
		label: "Movement",
		columns: [
			{ key: "dis_qty", label: "DIS QTY" },
			{ key: "rtv_qty", label: "RTV QTY" },
			{ key: "sec_sale_qty", label: "SEC-SALE QTY" },
			{ key: "net_qty", label: "NET QTY" },
		],
	},
	{
		id: "soh",
		label: "Stock on Hand",
		columns: [
			{ key: "cus_soh", label: "CUS-SOH" },
			{ key: "soh_net_qty", label: "NET QTY", highlight: true },
		],
	},
	{
		id: "rtpl_dis",
		label: "RTPL-DIS",
		columns: [
			{ key: "dis_git", label: "DIS-GIT", highlight: true },
			{ key: "rtv_git", label: "RTV-GIT", highlight: true },
		],
	},
	{
		id: "dis_ret",
		label: "DIS-RET",
		columns: [
			{ key: "dis_git_2", label: "DIS-GIT", highlight: true },
			{ key: "rtv_git_2", label: "RTV-GIT", highlight: true },
		],
	},
	{
		id: "git_net_qty",
		label: "NET QTY",
		spanRows: true,
		columns: [{ key: "git_net_qty", label: "NET QTY", highlight: true }],
	},
	{
		id: "settlement",
		label: "Settlement",
		columns: [
			{ key: "shortage_cn", label: "SHORTAGE-CN", highlight: true },
			{ key: "dispute_cn", label: "DISPUTE-CN", highlight: true },
		],
	},
];

const PINNED_VARIANCE = { key: "final_variance", label: "VARIANCE", highlight: true };

const FLAT_COLUMNS = COLUMN_GROUPS.flatMap((group) => group.columns);

const repeatedLabels = new Set(
	FLAT_COLUMNS.map((col) => col.label).filter((label, index, labels) => labels.indexOf(label) !== index)
);

const NET_KEYS = new Set(["net_qty", "soh_net_qty", "git_net_qty"]);

const TOTAL_COLUMNS = [
	...COLUMN_GROUPS.flatMap((group) =>
		group.columns
			.filter((col) => !NET_KEYS.has(col.key))
			.map((col) => ({
				key: col.key,
				label: repeatedLabels.has(col.label) ? `${group.label} ${col.label}` : col.label,
			}))
	),
	PINNED_VARIANCE,
];

const groupHeaderSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.5,
	textTransform: "uppercase",
	whiteSpace: "nowrap",
	bgcolor: "#f3f4f6",
	color: "#4b5563",
	borderBottom: "1px solid #e5e7eb",
	py: 0.75,
	px: 1.25,
	textAlign: "center",
	boxSizing: "border-box",
};

const HEADER_ROW_HEIGHT = 34;
const CUSTOMER_COL_MIN = 140;
const CUSTOMER_COL_MAX = 520;
const CUSTOMER_COL_DEFAULT = 220;

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.25,
	whiteSpace: "nowrap",
	bgcolor: "#fafafa",
	borderBottom: "1px solid #e5e7eb",
	py: 1,
	px: 1.25,
	color: "#374151",
	boxSizing: "border-box",
};

const bodyCellSx = {
	whiteSpace: "nowrap",
	py: 1.1,
	px: 1.25,
	fontSize: "0.8125rem",
	borderBottom: "1px solid #f1f5f9",
	color: "#111827",
};

const summaryView = {
	month: null,
	search: "",
	rows: [],
	page: 0,
	rowsPerPage: 25,
	customerColWidth: CUSTOMER_COL_DEFAULT,
};

const formatQty = (value) => {
	if (value === null || value === undefined || value === "") return "—";
	return Number(value).toLocaleString("en-IN");
};

const qtyColor = (value, highlight) => {
	if (!highlight || value === null || value === undefined || value === "") return "#111827";
	const num = Number(value);
	if (num === 0) return "#15803d";
	if (num < 0) return "#b91c1c";
	return "#b45309";
};

const compareCell = (a, b, numeric) => {
	const emptyA = a === null || a === undefined || a === "";
	const emptyB = b === null || b === undefined || b === "";
	if (emptyA && emptyB) return 0;
	if (emptyA) return 1;
	if (emptyB) return -1;
	if (numeric) return (Number(a) || 0) - (Number(b) || 0);
	return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
};

function SortHeader({ label, active, direction, align = "left", onClick }) {
	return (
		<TableSortLabel
			active={active}
			direction={active ? direction : "asc"}
			onClick={onClick}
			sx={{
				width: "100%",
				justifyContent: align === "right" ? "flex-end" : "flex-start",
				color: "inherit !important",
				fontWeight: "inherit",
				fontSize: "inherit",
				letterSpacing: "inherit",
				"&:hover": { color: "inherit" },
				"&.Mui-active": { color: "inherit" },
				"& .MuiTableSortLabel-icon": { fontSize: 14, color: "#6b7280 !important" },
			}}
		>
			{label}
		</TableSortLabel>
	);
}

function Summary() {
	const navigate = useNavigate();
	const { loginUser, setSnackBar, setSnackBarOpen } = GlobalState();
	const viewer = isMboLfsQtyViewer(loginUser);
	const [month, setMonth] = useState(() =>
		summaryView.month?.isValid?.() ? summaryView.month : dayjs().startOf("month")
	);
	const [search, setSearch] = useState(summaryView.search);
	const [rows, setRows] = useState(summaryView.rows);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(summaryView.page);
	const [rowsPerPage, setRowsPerPage] = useState(summaryView.rowsPerPage);
	const [sort, setSort] = useState({ key: "", dir: "asc" });
	const [customerColWidth, setCustomerColWidth] = useState(
		summaryView.customerColWidth || CUSTOMER_COL_DEFAULT
	);
	const skipPageReset = useRef(true);
	const customerResizeCleanup = useRef(null);

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const openCustomerDetail = (customerName) => {
		if (!customerName || !month?.isValid?.()) return;
		const params = new URLSearchParams({
			customer: customerName,
			month: month.format("YYYY-MM"),
		});
		navigate(`/${rootProtectedPath}/${services.mbo_lfs_qty_reco.path}/detail?${params.toString()}`);
	};

	const loadSummary = useCallback(
		async (selectedMonth) => {
			if (!selectedMonth?.isValid?.()) {
				showSnack("Select a month", "error");
				return;
			}
			setLoading(true);
			setRows([]);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/summary", {
					params: { month: selectedMonth.format("YYYY-MM") },
				});
				setRows(res?.data?.result?.rows || []);
			} catch (err) {
				const data = err?.response?.data;
				const message =
					(typeof data === "string" && data.trim()) ||
					data?.message ||
					err?.message ||
					"Failed to load summary";
				showSnack(message, "error");
			} finally {
				setLoading(false);
			}
		},
		[setSnackBar, setSnackBarOpen]
	);

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return rows;
		return rows.filter((row) => row.customer_name.toLowerCase().includes(q));
	}, [rows, search]);

	const sortedRows = useMemo(() => {
		if (!sort.key) return filteredRows;
		const numeric = sort.key !== "customer_name";
		const dir = sort.dir === "desc" ? -1 : 1;
		return [...filteredRows].sort(
			(left, right) => compareCell(left[sort.key], right[sort.key], numeric) * dir
		);
	}, [filteredRows, sort]);

	const toggleSort = (key, numeric) => {
		setSort((current) =>
			current.key === key
				? { key, dir: current.dir === "asc" ? "desc" : "asc" }
				: { key, dir: numeric ? "desc" : "asc" }
		);
		setPage(0);
	};

	const customerColSizeSx = {
		width: customerColWidth,
		minWidth: customerColWidth,
		maxWidth: customerColWidth,
	};

	const startCustomerResize = (event) => {
		event.preventDefault();
		event.stopPropagation();
		const startX = event.clientX;
		const startWidth = customerColWidth;
		const previousCursor = document.body.style.cursor;
		const previousSelect = document.body.style.userSelect;
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
		const onMove = (moveEvent) => {
			const next = Math.min(
				CUSTOMER_COL_MAX,
				Math.max(CUSTOMER_COL_MIN, startWidth + moveEvent.clientX - startX)
			);
			setCustomerColWidth(next);
		};
		const onUp = () => {
			document.body.style.cursor = previousCursor;
			document.body.style.userSelect = previousSelect;
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
			customerResizeCleanup.current = null;
		};
		customerResizeCleanup.current?.();
		customerResizeCleanup.current = onUp;
		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
	};

	useEffect(() => () => customerResizeCleanup.current?.(), []);

	useEffect(() => {
		summaryView.month = month;
		summaryView.search = search;
		summaryView.rows = rows;
		summaryView.page = page;
		summaryView.rowsPerPage = rowsPerPage;
		summaryView.customerColWidth = customerColWidth;
	}, [month, search, rows, page, rowsPerPage, customerColWidth]);

	useEffect(() => {
		if (skipPageReset.current) {
			skipPageReset.current = false;
			return;
		}
		setPage(0);
	}, [search, rows]);

	const pagedRows = useMemo(() => {
		const start = page * rowsPerPage;
		return sortedRows.slice(start, start + rowsPerPage);
	}, [sortedRows, page, rowsPerPage]);

	const columnTotals = useMemo(() => {
		const sum = (key) => filteredRows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
		return TOTAL_COLUMNS.map((col) => ({ ...col, value: sum(col.key) }));
	}, [filteredRows]);

	return (
		<Box>
			{/* Toolbar */}
			<Panel sx={{ px: 2, py: 1.5, mb: 1.5 }}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack
						direction={{ xs: "column", lg: "row" }}
						spacing={1.25}
						alignItems={{ xs: "stretch", lg: "center" }}
						justifyContent="space-between"
					>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={1.25}
							alignItems={{ xs: "stretch", sm: "center" }}
							flexWrap="wrap"
							useFlexGap
						>
							<DatePicker
								label="Month"
								views={["year", "month"]}
								openTo="month"
								value={month}
								onChange={(value) => setMonth(value ? value.startOf("month") : value)}
								format="MMM YYYY"
								slotProps={{
									textField: {
										size: "small",
										sx: {
											width: { xs: "100%", sm: 160 },
											flexShrink: 0,
											...compactInputSx,
										},
									},
								}}
							/>
							<TextField
								size="small"
								placeholder="Search customer"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								sx={{ minWidth: { xs: "100%", sm: 220 }, ...compactInputSx }}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
										</InputAdornment>
									),
								}}
							/>
						</Stack>
						<Stack direction="row" spacing={1} alignItems="center">
							<Button
								variant="contained"
								size="small"
								disabled={loading}
								onClick={() => loadSummary(month)}
								startIcon={
									loading ? <CircularProgress size={14} color="inherit" /> : <RefreshOutlinedIcon />
								}
								sx={{ ...primaryButtonSx, height: 36 }}
							>
								{loading ? "Running..." : "Run"}
							</Button>
							{viewer ? null : (
							<Button
								variant="outlined"
								size="small"
								disabled={loading || filteredRows.length === 0}
								startIcon={<DownloadOutlinedIcon />}
								onClick={() => {
									const columns = [
										{ key: "customer_name", label: "CUSTOMER NAME" },
										...COLUMN_GROUPS.flatMap((group) =>
											group.columns.map((col) => ({
												key: col.key,
												label: repeatedLabels.has(col.label) ? `${group.label} ${col.label}` : col.label,
											}))
										),
										{ key: PINNED_VARIANCE.key, label: PINNED_VARIANCE.label },
									];
									const monthName = month?.isValid?.() ? month.format("YYYY-MM") : "summary";
									downloadTable(`mbo_lfs_summary_${monthName}.xlsx`, columns, sortedRows);
								}}
								sx={{ ...outlinedButtonSx, height: 36 }}
							>
								Export
							</Button>
							)}
						</Stack>
					</Stack>
				</LocalizationProvider>
			</Panel>

			{/* KPI strip */}
			<Panel sx={{ mb: 1.5, display: "flex", flexWrap: "nowrap", width: "100%", overflow: "hidden" }}>
				<KpiStat label="Customers" value={filteredRows.length.toLocaleString("en-IN")} />
				{columnTotals.map((col) => (
					<KpiStat
						key={col.key}
						label={col.label}
						value={col.value.toLocaleString("en-IN")}
						tone={col.key === "final_variance" ? (col.value === 0 ? "success" : "danger") : "neutral"}
					/>
				))}
			</Panel>

			{/* Table */}
			<Panel sx={{ overflow: "hidden" }}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef0f3" }}
				>
					<Typography variant="subtitle2" fontWeight={700} color="#111827">
						Customer Qty Summary
					</Typography>
					<Chip
						label={`${filteredRows.length} customers`}
						size="small"
						sx={{
							height: 24,
							fontWeight: 600,
							fontSize: "0.7rem",
							bgcolor: "#f3f4f6",
							color: "#374151",
						}}
					/>
				</Stack>

				<TableContainer sx={{ maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
					<Table
						stickyHeader
						size="small"
						sx={{ minWidth: 1460 + customerColWidth, width: 1460 + customerColWidth }}
					>
						<colgroup>
							<col style={{ width: customerColWidth }} />
						</colgroup>
						<TableHead>
							<TableRow>
								<TableCell
									sortDirection={sort.key === "customer_name" ? sort.dir : false}
									sx={{
										...headerCellSx,
										position: "sticky",
										left: 0,
										top: 0,
										zIndex: 8,
										height: HEADER_ROW_HEIGHT,
										...customerColSizeSx,
										py: 0,
										lineHeight: `${HEADER_ROW_HEIGHT}px`,
										bgcolor: "#fafafa",
										borderRight: "1px solid #e5e7eb",
										borderBottom: 0,
										boxShadow: "2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: 0 },
									}}
								>
									<SortHeader
										label="CUSTOMER NAME"
										active={sort.key === "customer_name"}
										direction={sort.dir}
										onClick={() => toggleSort("customer_name", false)}
									/>
									<Box
										onMouseDown={startCustomerResize}
										onClick={(clickEvent) => clickEvent.stopPropagation()}
										sx={{
											position: "absolute",
											top: 0,
											right: 0,
											width: 8,
											height: HEADER_ROW_HEIGHT * 2,
											cursor: "col-resize",
											zIndex: 8,
											"&:hover": { bgcolor: "rgba(37, 99, 235, 0.35)" },
										}}
									/>
								</TableCell>
								{COLUMN_GROUPS.map((group) =>
									group.spanRows ? (
										<TableCell
											key={group.id}
											align="right"
											sortDirection={sort.key === group.columns[0].key ? sort.dir : false}
											sx={{
												...headerCellSx,
												height: HEADER_ROW_HEIGHT,
												py: 0,
												lineHeight: `${HEADER_ROW_HEIGHT}px`,
												borderLeft: "1px solid #e5e7eb",
												borderBottom: 0,
												minWidth: 96,
												bgcolor: "#fafafa",
												"&.MuiTableCell-stickyHeader": { top: 0 },
											}}
										>
											<SortHeader
												label={group.label}
												align="right"
												active={sort.key === group.columns[0].key}
												direction={sort.dir}
												onClick={() => toggleSort(group.columns[0].key, true)}
											/>
										</TableCell>
									) : (
										<TableCell
											key={group.id}
											align="center"
											colSpan={group.columns.length}
											sx={{
												...groupHeaderSx,
												height: HEADER_ROW_HEIGHT,
												py: 0,
												lineHeight: `${HEADER_ROW_HEIGHT}px`,
												borderLeft: "1px solid #e5e7eb",
												"&.MuiTableCell-stickyHeader": { top: 0 },
											}}
										>
											{group.label}
										</TableCell>
									)
								)}
								<TableCell
									align="right"
									sortDirection={sort.key === PINNED_VARIANCE.key ? sort.dir : false}
									sx={{
										...headerCellSx,
										position: "sticky",
										right: 0,
										zIndex: 6,
										height: HEADER_ROW_HEIGHT,
										minWidth: 108,
										width: 108,
										maxWidth: 108,
										px: 1,
										py: 0,
										lineHeight: `${HEADER_ROW_HEIGHT}px`,
										bgcolor: "#fafafa",
										borderLeft: "1px solid #e5e7eb",
										borderBottom: 0,
										boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: 0 },
									}}
								>
									<SortHeader
										label={PINNED_VARIANCE.label}
										align="right"
										active={sort.key === PINNED_VARIANCE.key}
										direction={sort.dir}
										onClick={() => toggleSort(PINNED_VARIANCE.key, true)}
									/>
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									sx={{
										...headerCellSx,
										position: "sticky",
										left: 0,
										zIndex: 6,
										height: HEADER_ROW_HEIGHT,
										...customerColSizeSx,
										py: 0,
										bgcolor: "#fafafa",
										borderRight: "1px solid #e5e7eb",
										boxShadow: "2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: HEADER_ROW_HEIGHT },
									}}
								/>
								{FLAT_COLUMNS.map((col, index) => {
									const spanned = COLUMN_GROUPS.some(
										(group) => group.spanRows && group.columns.some((item) => item.key === col.key)
									);
									return (
										<TableCell
											key={`${col.key}-${index}`}
											align="right"
											sortDirection={!spanned && sort.key === col.key ? sort.dir : false}
											sx={{
												...headerCellSx,
												height: HEADER_ROW_HEIGHT,
												py: 0,
												lineHeight: spanned ? undefined : `${HEADER_ROW_HEIGHT}px`,
												bgcolor: "#fafafa",
												borderLeft:
													index === 0 || COLUMN_GROUPS.some((g) => g.columns[0].key === col.key)
														? "1px solid #e5e7eb"
														: undefined,
												"&.MuiTableCell-stickyHeader": { top: HEADER_ROW_HEIGHT },
											}}
										>
											{spanned ? (
												""
											) : (
												<SortHeader
													label={col.label}
													align="right"
													active={sort.key === col.key}
													direction={sort.dir}
													onClick={() => toggleSort(col.key, true)}
												/>
											)}
										</TableCell>
									);
								})}
								<TableCell
									sx={{
										...headerCellSx,
										position: "sticky",
										right: 0,
										zIndex: 6,
										height: HEADER_ROW_HEIGHT,
										minWidth: 108,
										width: 108,
										maxWidth: 108,
										py: 0,
										bgcolor: "#fafafa",
										borderLeft: "1px solid #e5e7eb",
										boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: HEADER_ROW_HEIGHT },
									}}
								/>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={FLAT_COLUMNS.length + 2} align="center" sx={{ py: 8, borderBottom: 0 }}>
										<Stack alignItems="center" spacing={1.25}>
											<CircularProgress size={28} />
											<Typography color="text.secondary" variant="body2">
												Loading...
											</Typography>
										</Stack>
									</TableCell>
								</TableRow>
							) : (
								pagedRows.map((row) => (
								<TableRow
									key={row.id}
									hover
									sx={{
										"&:hover td": { bgcolor: "#f9fafb" },
										"&:last-child td": { borderBottom: 0 },
									}}
								>
									<TableCell
										sx={{
											...bodyCellSx,
											position: "sticky",
											left: 0,
											zIndex: 2,
											bgcolor: "#fff",
											fontWeight: 600,
											...customerColSizeSx,
											overflow: "hidden",
											borderRight: "1px solid #eef0f3",
											boxShadow: "2px 0 6px rgba(15,23,42,0.04)",
										}}
									>
										<Tooltip title={row.customer_name} placement="top" enterDelay={250}>
											<Box
												component="button"
												type="button"
												onClick={() => openCustomerDetail(row.customer_name)}
												sx={{
													display: "block",
													width: "100%",
													maxWidth: "100%",
													p: 0,
													border: 0,
													bgcolor: "transparent",
													cursor: "pointer",
													font: "inherit",
													fontWeight: 600,
													fontSize: "0.8125rem",
													color: "#1d4ed8",
													textAlign: "left",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{row.customer_name}
											</Box>
										</Tooltip>
									</TableCell>
									{FLAT_COLUMNS.map((col, index) => {
										const value = row[col.key];
										const isGroupStart =
											index === 0 || COLUMN_GROUPS.some((g) => g.columns[0].key === col.key);
										return (
											<TableCell
												key={col.key}
												align="right"
												sx={{
													...bodyCellSx,
													borderLeft: isGroupStart ? "1px solid #eef0f3" : undefined,
													fontWeight: col.highlight ? 600 : 500,
													color: qtyColor(value, col.highlight),
													fontVariantNumeric: "tabular-nums",
												}}
											>
												{formatQty(value)}
											</TableCell>
										);
									})}
									<TableCell
										align="right"
										sx={{
											...bodyCellSx,
											position: "sticky",
											right: 0,
											zIndex: 2,
											bgcolor: "#fff",
											fontWeight: 700,
											minWidth: 108,
											width: 108,
											maxWidth: 108,
											px: 1,
											borderLeft: "1px solid #eef0f3",
											boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
											color: qtyColor(row[PINNED_VARIANCE.key], true),
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{formatQty(row[PINNED_VARIANCE.key])}
									</TableCell>
								</TableRow>
							))
							)}
							{!loading && filteredRows.length === 0 && (
								<TableRow>
									<TableCell colSpan={FLAT_COLUMNS.length + 2} align="center" sx={{ py: 6 }}>
										<Typography color="text.secondary">No customers found</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredRows.length}
					page={page}
					onPageChange={(_, nextPage) => setPage(nextPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[25, 50, 100]}
				/>
			</Panel>
		</Box>
	);
}

export default Summary;
